import { gcm } from '@noble/ciphers/aes.js';

const KIOSK_TOKEN_KEY_HEX =
  '00112233445566778899aabbccddeeff00112233445566778899aabbccddeeff';

const TOKEN_IV_LENGTH = 12;

const hexToBytes = (hex: string): Uint8Array => {
  const bytes = new Uint8Array(hex.length / 2);

  for (let index = 0; index < hex.length; index += 2) {
    bytes[index / 2] = parseInt(hex.slice(index, index + 2), 16);
  }

  return bytes;
};

const base64UrlToBytes = (value: string): Uint8Array => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
  const binary = atob(padded);

  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const bytesToText = (bytes: Uint8Array): string => {
  return new TextDecoder().decode(bytes);
};

const textToBytes = (value: string): Uint8Array => {
  return new TextEncoder().encode(value);
};

const getKey = (): Uint8Array => {
  return hexToBytes(KIOSK_TOKEN_KEY_HEX);
};

/**
 * Encrypts a kiosk ID into a URL-safe AES-GCM token.
 *
 * Token format:
 * Base64URL(IV + ciphertext + authentication tag)
 */
export const encryptKioskToken = (
  kioskId: string,
): string => {
  const key = getKey();

  const iv = crypto.getRandomValues(
    new Uint8Array(TOKEN_IV_LENGTH),
  );

  const cipher = gcm(key, iv);

  const plaintext = textToBytes(kioskId);
  const ciphertext = cipher.encrypt(plaintext);

  const encryptedBytes = new Uint8Array(
    iv.length + ciphertext.length,
  );

  encryptedBytes.set(iv, 0);
  encryptedBytes.set(ciphertext, iv.length);

  return bytesToBase64Url(encryptedBytes);
};

/**
 * Decrypts a kiosk QR token and returns the kiosk ID.
 *
 * Token format:
 * Base64URL(IV + ciphertext + authentication tag)
 */
export const decryptKioskToken = (
  token: string,
): string => {
  const encryptedBytes = base64UrlToBytes(token);

  if (encryptedBytes.length <= TOKEN_IV_LENGTH) {
    throw new Error('Invalid kiosk token.');
  }

  const iv = encryptedBytes.slice(
    0,
    TOKEN_IV_LENGTH,
  );

  const ciphertext = encryptedBytes.slice(
    TOKEN_IV_LENGTH,
  );

  const key = getKey();

  const cipher = gcm(key, iv);

  const decrypted = cipher.decrypt(ciphertext);

  const kioskId = bytesToText(decrypted);

  if (!kioskId) {
    throw new Error('Kiosk ID is missing from token.');
  }

  return kioskId;
};